import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../../../../api';
import CRUDs from '../../../../api/CRUDs';
import { useMRTState } from '../../../../hooks/useMRTState';
import { useNavigateParams } from '../../../../hooks/useNavigateParams';
import { useActionLock } from '../../../../hooks/useActionLock';
import useFlashMessage from '../../../../hooks/useFlashMessage';

export function useListagemModule({ tipo, parametro, permissoes }) {
    const recurso = tipo ? 'arquivoDocumentoTipo' : 'arquivoDocumento';
    const rota = tipo ? '/ArquivoDocumentoTipo' : '/ArquivoDocumento';
    const mrt = useMRTState({ storageKey: `${recurso}.Index`, pageSizeDefault: Math.min(Number(parametro?.paginacao) || 5, 200) });
    const { columnFilters, columnFilterFns, globalFilter, sorting, pagination, setPagination, setRowCount, setIsLoading, setIsRefetching, setIsError } = mrt;
    const [dados, setDados] = useState([]), [selecionada, setSelecionada] = useState(null), [erro, setErro] = useState('');
    const [periodo, setPeriodo] = useState({ dataInicio: '', dataFim: '' });
    const request = useRef(0), carregou = useRef(false), filtrosRef = useRef('');
    const empresaId = localStorage.getItem('empresaId');
    const lock = useActionLock(), { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams(), location = useLocation();
    const carregar = useCallback(async () => {
        const atual = ++request.current;
        setIsLoading(!carregou.current); setIsRefetching(carregou.current); setErro('');
        try {
            if (periodo.dataInicio && periodo.dataFim && periodo.dataInicio > periodo.dataFim) throw new Error('A data inicial deve ser anterior ou igual à data final.');
            const { data } = await api.get(`/${recurso}/listar`, { params: {
                start: pagination.pageIndex * Math.min(pagination.pageSize, 200), size: Math.min(pagination.pageSize, 200),
                filters: JSON.stringify(columnFilters), filtersMode: JSON.stringify(columnFilterFns), sorting: JSON.stringify(sorting), globalFilter,
                ...(!tipo ? periodo : {}),
            } });
            if (atual !== request.current) return;
            if (!Array.isArray(data.data) || !Number.isFinite(data.meta?.totalRowCount)) throw new Error('Resposta inválida na consulta.');
            const ultima = Math.max(0, Math.ceil(data.meta.totalRowCount / Math.min(pagination.pageSize, 200)) - 1);
            if (pagination.pageIndex > ultima) { setPagination(p => ({ ...p, pageIndex: ultima })); return; }
            setDados(data.data); setRowCount(data.meta.totalRowCount); setIsError(false); carregou.current = true;
        } catch(e) { if (atual === request.current) { setDados([]); setRowCount(0); setIsError(true); setErro(e.response?.data?.message || e.message); } }
        finally { if (atual === request.current) { setIsLoading(false); setIsRefetching(false); } }
    }, [recurso, tipo, periodo, pagination.pageIndex, pagination.pageSize, columnFilters, columnFilterFns, globalFilter, sorting, setPagination, setRowCount, setIsLoading, setIsRefetching, setIsError]);
    useEffect(() => {
        if (!permissoes.autorizado) { setDados([]); return; }
        const chave = JSON.stringify([empresaId, columnFilters, columnFilterFns, globalFilter, sorting, periodo]);
        const mudou = filtrosRef.current && filtrosRef.current !== chave;
        filtrosRef.current = chave;
        if (mudou && pagination.pageIndex !== 0) { setPagination(p => ({ ...p, pageIndex: 0 })); return; }
        if (pagination.pageSize > 200) { setPagination(p => ({ ...p, pageSize: 200, pageIndex: 0 })); return; }
        carregar();
        return () => { request.current += 1; };
    }, [permissoes.autorizado, empresaId, columnFilters, columnFilterFns, globalFilter, sorting, periodo, pagination.pageIndex, pagination.pageSize, setPagination, carregar]);
    const abrir = row => {
        if (!(row ? permissoes.autorizado : permissoes.incluir) || lock.isLocked) return;
        const params = new URLSearchParams(location.search);
        if (row) params.set('uuid', row.uuid); else params.delete('uuid');
        params.delete('aba');
        params.set('btnText', row ? 'Atualizar' : 'Cadastrar');
        navigate(`${rota}/CreateOrEdit`, params);
    };
    const excluir = () => lock.run(async () => {
        if (!permissoes.cancelar || !selecionada) return;
        try {
            const { data } = await CRUDs[recurso].deletar({ uuid: selecionada.uuid });
            if (data.type !== 'success') throw new Error(data.message || 'Não foi possível excluir.');
            setFlashMessage(data.message, data.type); setSelecionada(null); await carregar();
        } catch(e) { setFlashMessage(e.response?.data?.message || e.message, 'error'); }
    });
    return { mrt, dados, selecionada, setSelecionada, abrir, excluir, excluindo: lock.isLocked, erro, periodo, setPeriodo, recarregar: carregar };
}
